/**
 * Browser-app paths must stay separate from route patterns.
 *
 * Route tables deliberately use domain-root-style paths such as `/orders/:id`.
 * This helper adapts those paths to the Vite application base at the browser
 * boundary, so the same route table works at `/` and below a preview sub-path.
 */
export interface AppBasePath {
  /** The canonical base path: exactly one leading and trailing slash. */
  readonly base: string;
  /** Removes this app's base before a route table matches a pathname. */
  strip(pathname: string): string;
  /** Adds this app's base to an internal route or public asset path. */
  prefix(path: string): string;
  /** Alias for `prefix` that makes asset call sites self-documenting. */
  asset(path: string): string;
}

/**
 * Normalizes a configured Vite base for comparisons and route matching.
 *
 * Relative Vite bases (`.` and `./`) intentionally normalize to `/`: they do
 * not contain a literal deploy prefix. `createAppBasePath` can recover that
 * prefix from the emitted module URL when one is provided.
 */
export function normalizeBasePath(base: string | undefined | null): string {
  const value = base?.trim() ?? '';
  if (value === '' || value === '.' || value === './') return '/';
  const path = value.replace(/^\/+|\/+$/g, '');
  return path === '' ? '/' : `/${path}/`;
}

function pathAndSuffix(path: string): { pathname: string; suffix: string } {
  const boundary = path.search(/[?#]/u);
  return boundary === -1
    ? { pathname: path, suffix: '' }
    : { pathname: path.slice(0, boundary), suffix: path.slice(boundary) };
}

function normalizeAppPath(path: string): string {
  const { pathname, suffix } = pathAndSuffix(path);
  const clean = pathname.replace(/^\/+/, '');
  return clean === '' ? suffix : `${clean}${suffix}`;
}

/**
 * Static hosts commonly expose their entry document at `/index.html`. The
 * document is the application root, not a customer-facing route, so normalize
 * it after the deploy base is removed. Other `.html` paths remain untouched.
 */
function normalizeEntryDocument(pathname: string): string {
  return pathname === '/index.html' ? '/' : pathname;
}

/**
 * Creates a base adapter for a Vite browser application.
 *
 * With Vite's portable `base: './'`, `BASE_URL` deliberately contains no
 * absolute prefix. In a production bundle, `moduleUrl` is an emitted
 * `/base/assets/*.js` URL, so its parent directory recovers the real app base
 * without hard-coding a staging path. Domain-root builds remain `/`.
 */
export function createAppBasePath(
  viteBase: string | undefined | null,
  moduleUrl?: string,
): AppBasePath {
  const configuredBase = normalizeBasePath(viteBase);
  const isRelativeViteBase = viteBase?.trim() === '.' || viteBase?.trim() === './';
  const resolvedBase =
    isRelativeViteBase && moduleUrl !== undefined
      ? normalizeBasePath(new URL('..', moduleUrl).pathname)
      : configuredBase;

  return {
    base: resolvedBase,
    strip(pathname: string): string {
      const normalizedPathname = normalizeBasePath(pathname).replace(/\/$/u, '') || '/';
      if (resolvedBase === '/') return normalizeEntryDocument(normalizedPathname);
      const baseWithoutTrailingSlash = resolvedBase.slice(0, -1);
      if (normalizedPathname === baseWithoutTrailingSlash) return '/';
      const stripped = normalizedPathname.startsWith(resolvedBase)
        ? `/${normalizedPathname.slice(resolvedBase.length)}`
        : normalizedPathname;
      return normalizeEntryDocument(stripped);
    },
    prefix(path: string): string {
      const normalizedPath = normalizeAppPath(path);
      if (normalizedPath === '') return resolvedBase;
      return resolvedBase === '/' ? `/${normalizedPath}` : `${resolvedBase}${normalizedPath}`;
    },
    asset(path: string): string {
      return this.prefix(path);
    },
  };
}
